/**
 * ApprovalCardActionBar component.
 *
 * Renders the action buttons at the bottom of the approval card.
 *
 * Layout:
 *   [APPROVE (⌘A)]  [SUPPRESS (⌘S)]  [REJECT (⌘R)]
 *
 * Behavior:
 * - Resolution buttons disabled when the item is not owned by the current operator.
 * - Keyboard shortcut hints shown inline.
 * - Approve: fires resolution with "approved".
 * - Suppress: shows confirm modal then fires resolution with "suppressed".
 * - Reject: opens the reject dialog.
 * - Edit button appears only when `supports_edit: true` (between Approve and Suppress).
 */

import * as React from 'react'
import type { CSSProperties } from 'react'
import { Check, Ban, X, Edit } from 'lucide-react'
import {
  Button,
  Kbd,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui'
import type { ToolButtonConfig } from '../../types'

export interface ApprovalCardActionBarProps {
  /** Whether the current operator owns this item. */
  isOwned: boolean
  /** Whether the item supports edit-before-approve. */
  supportsEdit: boolean
  /** Called when Approve is clicked. */
  onApprove: () => void
  /** Called when Suppress is confirmed (or immediately for Dismiss). */
  onSuppress: () => void
  /** Called when Reject is clicked. Opens the reject dialog. */
  onReject: () => void
  /** Called when Edit is clicked. Opens edit mode. */
  onEdit?: () => void
  /** Whether an action is currently pending. */
  isPending?: boolean
  /** Which action is pending (for showing loading state). */
  pendingAction?: 'approve' | 'suppress' | 'reject' | null
  /**
   * Where the action bar is positioned inside the card.
   *
   * `top`: visually attaches to the bottom of the header (no top border,
   * adds a bottom divider). Used when buttons sit immediately under the
   * header so operators can act without scrolling.
   *
   * `bottom`: visually attaches to the top of the card footer (top
   * border, no bottom divider). Legacy position.
   *
   * Defaults to `bottom` so existing callers are unaffected.
   */
  position?: 'top' | 'bottom'
  /**
   * Per-tool button configuration from the UI registry.
   *
   * Controls which buttons are shown and how they are labelled:
   * - `hide_approve`: hides the Approve button entirely
   * - `reject_label`: relabels the Reject button (e.g. "Respond")
   * - `suppress_label`: when set, replaces the Suppress+confirm-dialog
   *   flow with a direct-dismiss button using this label (e.g. "Dismiss")
   */
  buttonConfig?: ToolButtonConfig
}

export function ApprovalCardActionBar({
  isOwned: _isOwned,
  supportsEdit,
  onApprove,
  onSuppress,
  onReject,
  onEdit,
  isPending = false,
  pendingAction = null,
  position = 'bottom',
  buttonConfig,
}: ApprovalCardActionBarProps) {
  const [suppressDialogOpen, setSuppressDialogOpen] = React.useState(false)

  // Derived labels / visibility from button config
  const hideApprove = buttonConfig?.hide_approve === true
  const rejectLabel = buttonConfig?.reject_label ?? 'Reject'
  // When suppress_label is set, we show a direct-dismiss button (no confirm dialog).
  // When absent, we show the standard Suppress button with its confirmation dialog.
  const suppressLabel = buttonConfig?.suppress_label

  const handleSuppressClick = React.useCallback(() => {
    setSuppressDialogOpen(true)
  }, [])

  const handleSuppressConfirm = React.useCallback(() => {
    setSuppressDialogOpen(false)
    onSuppress()
  }, [onSuppress])

  const handleSuppressCancel = React.useCallback(() => {
    setSuppressDialogOpen(false)
  }, [])

  // The legacy claim/release flow has been retired — items arrive
  // unclaimed (owner_id is null) from the unified /v1/attention
  // endpoint. Actions should be enabled when the item is unclaimed
  // OR when it is owned by the current operator. Only block when
  // another operator has claimed the item.
  const isDisabled = isPending

  // Keyboard shortcut handlers
  React.useEffect(() => {
    if (suppressDialogOpen || isDisabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger when typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return
      }
      // All shortcuts require Cmd (Mac) or Ctrl (Windows/Linux) to prevent
      // accidental activation while the operator is navigating the queue.
      if (!e.metaKey && !e.ctrlKey) return

      switch (e.key.toLowerCase()) {
        case 'a':
          if (!hideApprove) {
            e.preventDefault()
            onApprove()
          }
          break
        case 's':
          e.preventDefault()
          // Dismiss flow: call onSuppress directly without a confirm dialog
          if (suppressLabel !== undefined) {
            onSuppress()
          } else {
            handleSuppressClick()
          }
          break
        case 'r':
          e.preventDefault()
          onReject()
          break
        case 'e':
          if (supportsEdit && onEdit) {
            e.preventDefault()
            onEdit()
          }
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [suppressDialogOpen, isDisabled, hideApprove, suppressLabel, onApprove, onSuppress, onReject, handleSuppressClick, supportsEdit, onEdit])

  const footerStyle = position === 'top' ? styles.footerTop : styles.footer

  return (
    <>
      <footer style={footerStyle}>
        {/* Approve button — hidden when button_config.hide_approve is true */}
        {!hideApprove && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                onClick={onApprove}
                disabled={isDisabled}
                aria-keyshortcuts="Meta+a"
                style={styles.approveButton}
                className="action-button-approve"
              >
                {pendingAction === 'approve' ? (
                  'Approving...'
                ) : (
                  <>
                    <Check style={styles.buttonIcon} aria-hidden="true" />
                    Approve
                    <Kbd style={styles.kbd}>⌘A</Kbd>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Approve this action (⌘A)</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Edit button (only when supports_edit: true) */}
        {supportsEdit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                onClick={onEdit}
                disabled={isDisabled || !onEdit}
                aria-keyshortcuts="Meta+e"
                style={styles.editButton}
              >
                <Edit style={styles.buttonIcon} aria-hidden="true" />
                Edit
                <Kbd style={styles.kbd}>⌘E</Kbd>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Edit before approving (⌘E)</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/*
          Button layout splits based on mode:
          - Respond mode (suppress_label set, e.g. escalate_to_operator):
              [Respond (green, ⌘R)]  [Dismiss (neutral, ⌘S)]
            Respond is the primary action so it leads; Dismiss is secondary.
          - Standard mode (no suppress_label):
              [Suppress (neutral, ⌘S)]  [Reject (red, ⌘R)]
            Suppress has a confirmation dialog; Reject opens the feedback dialog.
        */}
        {suppressLabel !== undefined ? (
          <>
            {/* Respond button — green primary action in escalate_to_operator */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="default"
                  onClick={onReject}
                  disabled={isDisabled}
                  aria-keyshortcuts="Meta+r"
                  style={styles.approveButton}
                >
                  {pendingAction === 'reject' ? (
                    `${rejectLabel}ing...`
                  ) : (
                    <>
                      <X style={styles.buttonIcon} aria-hidden="true" />
                      {rejectLabel}
                      <Kbd style={styles.kbd}>⌘R</Kbd>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{rejectLabel} with feedback (⌘R)</p>
              </TooltipContent>
            </Tooltip>

            {/* Dismiss button — secondary, no confirm dialog */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={onSuppress}
                  disabled={isDisabled}
                  aria-keyshortcuts="Meta+s"
                  style={styles.suppressButton}
                >
                  {pendingAction === 'suppress' ? (
                    'Dismissing...'
                  ) : (
                    <>
                      <Ban style={styles.buttonIcon} aria-hidden="true" />
                      {suppressLabel}
                      <Kbd style={styles.kbd}>⌘S</Kbd>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{suppressLabel} without responding (⌘S)</p>
              </TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            {/* Standard Suppress button — opens confirm dialog */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="secondary"
                  onClick={handleSuppressClick}
                  disabled={isDisabled}
                  aria-keyshortcuts="Meta+s"
                  style={styles.suppressButton}
                >
                  {pendingAction === 'suppress' ? (
                    'Suppressing...'
                  ) : (
                    <>
                      <Ban style={styles.buttonIcon} aria-hidden="true" />
                      Suppress
                      <Kbd style={styles.kbd}>⌘S</Kbd>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Skip without teaching the agent (⌘S)</p>
              </TooltipContent>
            </Tooltip>

            {/* Reject button — red, label overridden by button_config.reject_label */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="destructive"
                  onClick={onReject}
                  disabled={isDisabled}
                  aria-keyshortcuts="Meta+r"
                  style={styles.actionButton}
                >
                  {pendingAction === 'reject' ? (
                    `${rejectLabel}ing...`
                  ) : (
                    <>
                      <X style={styles.buttonIcon} aria-hidden="true" />
                      {rejectLabel}
                      <Kbd style={styles.kbd}>⌘R</Kbd>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{rejectLabel} with feedback (⌘R)</p>
              </TooltipContent>
            </Tooltip>
          </>
        )}

      </footer>

      {/* Suppress confirmation dialog — only used in the standard (non-dismiss) flow */}
      {suppressLabel === undefined && (
        <SuppressConfirmDialog
          open={suppressDialogOpen}
          onConfirm={handleSuppressConfirm}
          onCancel={handleSuppressCancel}
          isPending={pendingAction === 'suppress'}
        />
      )}
    </>
  )
}

interface SuppressConfirmDialogProps {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  isPending: boolean
}

/**
 * Suppress confirmation modal.
 *
 * "Suppress this tool call without sending feedback?"
 */
function SuppressConfirmDialog({
  open,
  onConfirm,
  onCancel,
  isPending,
}: SuppressConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent style={styles.dialogContent}>
        <DialogHeader>
          <DialogTitle>Suppress this tool call?</DialogTitle>
          <DialogDescription>
            This will skip approval for this specific instance without teaching
            the agent.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter style={styles.dialogFooter}>
          <Button variant="secondary" onClick={onCancel} disabled={isPending}>
            Cancel
            <Kbd style={styles.kbd}>Esc</Kbd>
          </Button>
          <Button variant="default" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Suppressing...' : 'Suppress'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const styles: Record<string, CSSProperties> = {
  footer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    borderTop: '1px solid var(--border-default)',
    paddingTop: '16px',
  },
  // Top-positioned action bar. Sticky so the Approve/Suppress/Reject
  // controls stay reachable while operators scroll through long
  // execution summaries — previously a long Customer Impact paragraph
  // pushed the buttons off-screen and forced a scroll-back to act.
  //
  // `top: 0` sticks to the nearest scrolling ancestor (the panel's
  // scroll body in sidebar mode, the maximized dialog body in modal
  // mode). The opaque background + bottom border prevents content
  // bleed-through as it scrolls underneath.
  footerTop: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '8px',
    borderBottom: '1px solid var(--border-default)',
    paddingTop: '4px',
    paddingBottom: '12px',
    position: 'sticky',
    top: 0,
    zIndex: 2,
    background: 'var(--bg-secondary)',
  },
  // All three resolution buttons share the same size and visual weight
  // so they read as peers. Approve carries semantic colour (success),
  // Reject carries destructive colour, and Suppress is a neutral
  // outline with strong contrast — previously it was a chip-grey
  // secondary and looked disabled next to the saturated fills.
  approveButton: {
    minWidth: '128px',
    height: '38px',
    background: 'var(--success)',
    color: 'white',
    fontWeight: 600,
  },
  editButton: {
    minWidth: '108px',
    height: '38px',
    fontWeight: 600,
  },
  actionButton: {
    minWidth: '128px',
    height: '38px',
    fontWeight: 600,
  },
  // Suppress button — outlined neutral. Stronger border than the
  // default secondary chip so it reads as an actionable peer to
  // Approve/Reject rather than a passive chip.
  suppressButton: {
    minWidth: '128px',
    height: '38px',
    fontWeight: 600,
    background: 'transparent',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
  },
  buttonIcon: {
    width: '15px',
    height: '15px',
    marginRight: '6px',
  },
  kbd: {
    marginLeft: '8px',
  },
  dialogContent: {
    maxWidth: '448px',
  },
  dialogFooter: {
    display: 'flex',
    gap: '8px',
  },
}

export default ApprovalCardActionBar
